import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import {
  type InsuranceAttrName,
  type PerStateInsurance,
  type InsuranceCitation,
} from "@/data/per-state-insurance";

/**
 * apps/web :: components :: InsuranceAttributeCard
 *
 * Renders one of the seven canonical insurance attributes for the
 * rule-diff browse page. Visually consistent with
 * `RuleDiffAttributeCard` (left-border + heading + value + reviewer
 * note + citation footer) but never carries a severity-tinted border,
 * because insurance facts are state-requirements rather than
 * country-comparison diffs.
 *
 * Card styling: same rounded box, left-border indicator, and footer
 * citation pattern as `RuleDiffAttributeCard`. The border uses the
 * brand-accent neutral so the section reads as informational rather
 * than diff-classified.
 *
 * Accessibility (WCAG 2.2 AA):
 *   - `<section>` with `aria-labelledby` → attribute heading id.
 *   - Citation link `target="_blank"` + `rel="noopener noreferrer"` +
 *     sr-only "(opens in new tab)" hint.
 *   - All copy resolves through i18next; no string literals in JSX.
 */

export interface InsuranceAttributeCardProps {
  /** The full per-state insurance entry; the card pulls only the
   *  attribute it needs but accepts the whole entry to avoid awkward
   *  per-attribute prop wiring. */
  readonly entry: PerStateInsurance;
  readonly attr: InsuranceAttrName;
}

/** Format a USD amount as e.g. "$25,000". Locale-agnostic to keep the
 *  citation faithful — figures on state DOI pages are always quoted
 *  in USD with US grouping. */
function formatUsd(amount: number): string {
  return `$${amount.toLocaleString("en-US")}`;
}

/** Title-case a snake_case enum value. Mirrors `formatAttrValue` in
 *  rulediff-seed-api but tuned for the insurance enum value spaces. */
function titleCase(snake: string): string {
  return snake
    .split("_")
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(" ");
}

interface ResolvedFacet {
  readonly displayValue: string;
  readonly translationKeyForValue: string;
  readonly citation: InsuranceCitation;
}

function resolveFacet(
  entry: PerStateInsurance,
  attr: InsuranceAttrName,
): ResolvedFacet {
  switch (attr) {
    case "mandatoryAtRegistration": {
      const v = entry.mandatoryAtRegistration.value;
      return {
        displayValue: titleCase(v),
        translationKeyForValue: `ruleDiffBrowse.insurance.value.mandatoryAtRegistration.${v}`,
        citation: entry.mandatoryAtRegistration.citation,
      };
    }
    case "minBodilyInjuryPerPerson":
      return {
        displayValue: formatUsd(entry.minBodilyInjuryPerPerson.amountUsd),
        translationKeyForValue: "",
        citation: entry.minBodilyInjuryPerPerson.citation,
      };
    case "minBodilyInjuryPerAccident":
      return {
        displayValue: formatUsd(entry.minBodilyInjuryPerAccident.amountUsd),
        translationKeyForValue: "",
        citation: entry.minBodilyInjuryPerAccident.citation,
      };
    case "minPropertyDamage":
      return {
        displayValue: formatUsd(entry.minPropertyDamage.amountUsd),
        translationKeyForValue: "",
        citation: entry.minPropertyDamage.citation,
      };
    case "uninsuredMotorist": {
      const v = entry.uninsuredMotorist.value;
      return {
        displayValue: titleCase(v),
        translationKeyForValue: `ruleDiffBrowse.insurance.value.uninsuredMotorist.${v}`,
        citation: entry.uninsuredMotorist.citation,
      };
    }
    case "pipNoFault": {
      const v = entry.pipNoFault.value;
      return {
        displayValue: titleCase(v),
        translationKeyForValue: `ruleDiffBrowse.insurance.value.pipNoFault.${v}`,
        citation: entry.pipNoFault.citation,
      };
    }
  }
}

export default function InsuranceAttributeCard({
  entry,
  attr,
}: InsuranceAttributeCardProps) {
  const { t, i18n } = useTranslation();
  const facet = useMemo(() => resolveFacet(entry, attr), [entry, attr]);
  const headingId = `rule-diff-insurance-${entry.stateCode}-${attr}`;

  // Resolve a translated value where one exists; fall back to the
  // formatted display value (USD or title-cased enum) so we never
  // render a raw key.
  const valueText = facet.translationKeyForValue
    ? t(facet.translationKeyForValue, { defaultValue: facet.displayValue })
    : facet.displayValue;

  // Reviewer note from the citation is the authoritative descriptive
  // string; it ships in English. ES translations of these notes are
  // not yet in the corpus (they're SME-attributable). We expose the
  // English note as the descriptive paragraph but pick an i18n header
  // and footer so the user lands in their language for navigation.
  const reviewerNote = facet.citation.note ?? "";

  return (
    <section
      data-testid={`rule-diff-insurance-card-${attr}`}
      aria-labelledby={headingId}
      className="rounded-md border-l-2 border-neutral-300 bg-white p-md sm:p-lg"
    >
      <div className="flex flex-col gap-sm sm:flex-row sm:items-baseline sm:justify-between">
        <h3
          id={headingId}
          className="font-display text-lg font-semibold text-brand-primary sm:text-xl"
        >
          {t(`ruleDiffBrowse.insurance.attr.${attr}`, { defaultValue: attr })}
        </h3>
      </div>

      <div className="mt-sm text-base">
        <span className="text-neutral-600">
          {t("ruleDiffBrowse.valueLabel")}:
        </span>{" "}
        <span className="font-semibold text-neutral-900">{valueText}</span>
      </div>

      {reviewerNote && (
        <p className="mt-sm text-sm text-neutral-800" lang="en">
          {reviewerNote}
        </p>
      )}

      <footer className="mt-md flex flex-wrap items-center gap-md text-xs text-neutral-600">
        <span>
          {t("ruleDiffBrowse.insurance.retrievedLabel")}:{" "}
          {facet.citation.retrievedOn}
        </span>
        <a
          href={facet.citation.url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-brand-primary underline-offset-2 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
        >
          {t("ruleDiffBrowse.sourceLink")}
          <span className="sr-only"> {t("ruleDiffBrowse.opensInNewTab")}</span>
        </a>
      </footer>
      {/*
        Locale-aware reading direction: the card content is left-aligned
        in LTR locales by default and reflows automatically in RTL
        because we use logical Tailwind tokens.
        i18n hint: i18n.language reflects current locale.
      */}
      <span className="sr-only">{i18n.language}</span>
    </section>
  );
}
